import { useState, useCallback, useEffect } from "react";

export const USERNAME_MIN_LENGTH = 2;
export const USERNAME_MAX_LENGTH = 20;
export const PROFILE_TEXT_MAX_LENGTH = 2500;
export const MIN_AGE = 16;

export enum Gender {
  FEMALE = "female",
  MALE = "male",
  NON_BINARY = "non_binary",
}

export interface UploadedImage {
  uri: string;
  object_name: string;
  get_url: string;
  progress: number;
}

export interface SignupState {
  step: number;
  name: string;
  gender: Gender;
  birthdate: Date | null;
  profileText: string;
  images: UploadedImage[];
}

const STORAGE_KEY = "flateroo-signup-store";

// Calculate age from birthdate
const calculateAge = (birthdate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const monthDiff = today.getMonth() - birthdate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthdate.getDate())
  ) {
    age--;
  }
  return age;
};

const getInitialState = (): SignupState => {
  // Try to load from localStorage
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert date strings back to Date objects
        return {
          ...parsed,
          birthdate: parsed.birthdate ? new Date(parsed.birthdate) : null,
        };
      } catch (e) {
        console.error("Error parsing saved signup data:", e);
      }
    }
  }

  return {
    step: 0,
    name: "",
    gender: Gender.FEMALE,
    birthdate: null,
    profileText: "",
    images: [],
  };
};

export const useSignupStore = () => {
  const [state, setState] = useState<SignupState>(getInitialState);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Navigation
  const setStep = useCallback((step: number) => {
    setState((s) => ({ ...s, step }));
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => ({ ...s, step: s.step + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => ({ ...s, step: Math.max(0, s.step - 1) }));
  }, []);

  // Setters
  const setName = useCallback((name: string) => {
    setState((s) => ({ ...s, name }));
  }, []);

  const setGender = useCallback((gender: Gender) => {
    setState((s) => ({ ...s, gender }));
  }, []);

  const setBirthdate = useCallback((birthdate: Date | null) => {
    setState((s) => ({ ...s, birthdate }));
  }, []);

  const setProfileText = useCallback((profileText: string) => {
    setState((s) => ({ ...s, profileText }));
  }, []);

  const setImages = useCallback(
    (value: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])) => {
      setState((s) => ({
        ...s,
        images: typeof value === "function" ? value(s.images) : value,
      }));
    },
    [],
  );

  const addImage = useCallback((image: UploadedImage) => {
    setState((s) => ({ ...s, images: [...s.images, image] }));
  }, []);

  const removeImage = useCallback((index: number) => {
    setState((s) => ({
      ...s,
      images: s.images.filter((_, i) => i !== index),
    }));
  }, []);

  // Validation
  const isStepValid = useCallback(
    (stepIndex: number): boolean => {
      switch (stepIndex) {
        case 0: // Name step
          return (
            state.name.trim().length >= USERNAME_MIN_LENGTH &&
            state.name.trim().length <= USERNAME_MAX_LENGTH
          );
        case 1: // Images step
          return (
            state.images.length >= 1 &&
            state.images.every((img) => img.progress === 1)
          );
        case 2: // Birthdate step
          if (!state.birthdate) return false;
          const age = calculateAge(state.birthdate);
          return (
            age >= MIN_AGE &&
            state.birthdate.getFullYear() >= 1920 &&
            state.birthdate <= new Date()
          );
        case 3: // Profile text step (optional)
          return state.profileText.length <= PROFILE_TEXT_MAX_LENGTH;
        case 4: // Final step
          return true;
        default:
          return false;
      }
    },
    [state],
  );

  // Reset
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      step: 0,
      name: "",
      gender: Gender.FEMALE,
      birthdate: null,
      profileText: "",
      images: [],
    });
  }, []);

  return {
    ...state,
    setStep,
    nextStep,
    prevStep,
    setName,
    setGender,
    setBirthdate,
    setProfileText,
    setImages,
    addImage,
    removeImage,
    isStepValid,
    reset,
  };
};

export type SignupStore = ReturnType<typeof useSignupStore>;
